import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssessmentAttempterService } from '../assessment-attempter.service';
import { interval, Subscription } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminApiService } from '../services/admin-api.service';
import { ASSESSMENT_START_RULES } from '../widgets/assessment-rules/assessment-rules.component';
import {
  isAssessmentViewportSupported,
  MIN_ASSESSMENT_VIEWPORT_WIDTH
} from '../assessment-device-support';
import { AssessmentProctoringService } from '../services/assessment-proctoring.service';

const QUESTION_TIMER_SECONDS = 120; // 2 minutes per question
const RING_CIRCUMFERENCE = 150.796; // 2 * PI * 24
const PROCTORING_STARTED_AT_KEY = 'assessmentProctoringStartedAt';
const ASSESSMENT_DURATION_SECONDS_KEY = 'assessmentDurationSeconds';
const ASSESSMENT_ENDS_AT_KEY = 'assessmentEndsAt';
const QUESTION_ENDS_AT_KEY = 'assessmentQuestionEndsAt';

@Component({
  selector: 'app-assessment-attempt',
  templateUrl: './assessment-attempt.component.html',
  styleUrls: ['./assessment-attempt.component.css'],
  standalone: false
})
export class AssessmentAttemptComponent implements OnInit, OnDestroy {

  @ViewChild('topBar') topBar?: ElementRef<HTMLElement>;
  @ViewChild('proctoringVideo') proctoringVideo?: ElementRef<HTMLVideoElement>;

  startRules = ASSESSMENT_START_RULES;

  // ── Overall timer ──
  timerSubscription: any;
  totalTime: number = 0;
  currentMinutes: number = Math.floor(this.totalTime / 60);
  currentSeconds: number = this.totalTime % 60;

  // ── Per-question timer ──
  questionTimerSubscription: any;
  questionTotalSeconds: number = QUESTION_TIMER_SECONDS;
  questionMinutes: number = Math.floor(QUESTION_TIMER_SECONDS / 60);
  questionSeconds: number = QUESTION_TIMER_SECONDS % 60;
  questionTimerDashOffset: number = 0;

  // ── UI state ──
  isFullscreen: boolean = false;
  isLoading: boolean = false;
  loadingText: string = 'Progressing...';
  questionAnimating: boolean = false;
  questionExiting: boolean = false;
  completedAnsweredCount: number = 0;
  completionError: string = '';
  isTimerDocked: boolean = false;
  integrityFailureSubmitted: boolean = false;
  integrityFailureMessage: string = '';
  isUnsupportedScreen: boolean = false;
  readonly minimumAssessmentViewportWidth = MIN_ASSESSMENT_VIEWPORT_WIDTH;
  isRequestingCamera: boolean = false;
  isCameraActive: boolean = false;
  cameraRequiredForResume: boolean = false;
  cameraErrorMessage: string = '';
  isTimerRecoveryLoading: boolean = false;
  timerRecoveryError: string = '';

  subscriptionVal: any;
  private proctoringSubscriptions = new Subscription();
  private overallTimerExpiryHandled = false;
  private questionTimerExpiryHandled = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private attempterService: AssessmentAttempterService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private adminApi: AdminApiService,
    private proctoringService: AssessmentProctoringService

  ) {}

  ngOnInit(): void {
    this.proctoringSubscriptions.add(
      this.proctoringService.uploadFailed$.subscribe(() => {
        this.snackBar.open(
          'A camera check could not be uploaded. We will retry the remaining checks.',
          'Dismiss',
          { duration: 5000 }
        );
      })
    );
    this.proctoringSubscriptions.add(
      this.proctoringService.cameraStopped$.subscribe(() => {
        this.isCameraActive = false;
        if (this.getStateOfAssessment() === 'question') {
          this.failAttemptForCameraInterruption();
        }
      })
    );

    if (!this.hasSupportedAssessmentViewport()) {
      this.isUnsupportedScreen = true;
      return;
    }

    this.restoreAssessmentDurationFromStorage();

    const assessmentId = this.route.snapshot.queryParamMap.get('assessmentId');
    const sessionId = localStorage.getItem('sessionId');
    const userId = this.authService.getCurrentUser()?.userId ?? null;;

    const assessmentState = localStorage.getItem('currentState');

    if (!assessmentState && !sessionId) {
      this.attempterService.startAssessment(assessmentId, userId).subscribe({ next: (data: any) => {
        if (data && 'sessionId' in data) {
          const sessionId: string = data['sessionId'].toString();
          localStorage.setItem('sessionId', sessionId);

          if (data['assessmentState'] === 'INPROGRESS' && data['currentQuestion']?.questionId) {
            this.applyAssessmentTiming(data);
            const recoveredState = this.createAssessmentState();
            recoveredState.currentQuestion = data['currentQuestion'];
            localStorage.setItem('currentState', JSON.stringify(recoveredState));
            this.attempterService.publishQuestionObject(JSON.stringify(data['currentQuestion']));
            this.cameraRequiredForResume = true;
            return;
          }

          localStorage.setItem('currentState', JSON.stringify({
            toDisplay: "rule",
            rules: this.startRules,
            nextStep: "pursue-session"
          }));
          this.attempterService.publishRulesObject(JSON.stringify(this.startRules));

          if ('assessmentTimeline' in data) {
            this.setAssessmentDurationMinutes(data['assessmentTimeline']);
          }
        } else {
          window.alert("Unable to start your assessment");
        }
      }, error: (err) => {
        if (err?.status === 412 && err?.error?.code === 'RESUME_REQUIRED') {
          this.router.navigate(['/profile'], {
            queryParams: { required: true, assessmentId }
          });
          return;
        }
        const message = err?.status === 403
          ? 'This assessment attempt is blocked. Please contact your administrator.'
          : 'Unable to start your assessment.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
        this.goToAssessments();
      }});
    } else {
      if (this.getStateOfAssessment() === 'failed') {
        this.integrityFailureSubmitted = true;
        this.integrityFailureMessage = 'This assessment attempt was ended after an integrity violation.';
      } else if (this.getStateOfAssessment() === 'question') {
        this.cameraRequiredForResume = true;
        this.restoreAssessmentTimingFromBackend(userId, sessionId);
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanupTimers();
    this.stopProctoring();
    this.proctoringSubscriptions.unsubscribe();
    this.exitFullscreen();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (!document.hidden || !this.shouldEnforceAssessmentVisibility()) return;
    this.failAttemptForIntegrityViolation();
  }

  private shouldEnforceAssessmentVisibility(): boolean {
    return window.location.hostname === 'app.konvac.com'
      && !this.integrityFailureSubmitted
      && this.getStateOfAssessment() === 'question';
  }

  private failAttemptForIntegrityViolation(): void {
    this.failAttempt('TAB_HIDDEN', 'The assessment was ended because you left the assessment tab.');
  }

  private failAttemptForCameraInterruption(): void {
    this.failAttempt('CAMERA_STOPPED', 'The assessment was ended because camera access was interrupted.');
  }

  private failAttempt(reason: 'TAB_HIDDEN' | 'CAMERA_STOPPED', message: string): void {
    const userId = this.authService.getCurrentUser()?.userId || localStorage.getItem('userId');
    const sessionId = localStorage.getItem('sessionId');
    if (!userId || !sessionId) return;

    this.integrityFailureSubmitted = true;
    this.integrityFailureMessage = message;
    this.cleanupTimers();
    this.stopProctoring();
    this.exitFullscreen();
    const currentState = this.createAssessmentState();
    currentState.toDisplay = 'failed';
    localStorage.setItem('currentState', JSON.stringify(currentState));
    this.clearPersistedAssessmentTiming();
    this.attempterService.failAssessmentAttempt(userId, sessionId, reason);
    this.cdr.detectChanges();
  }

  @HostListener('window:scroll')
  onAssessmentScroll(): void {
    const topBar = this.topBar?.nativeElement;
    if (!topBar) {
      this.isTimerDocked = false;
      return;
    }

    this.isTimerDocked = topBar.getBoundingClientRect().bottom <= 24;
  }

  cleanupTimers(): void {
    if (this.timerSubscription) this.timerSubscription.unsubscribe();
    if (this.questionTimerSubscription) this.questionTimerSubscription.unsubscribe();
  }

  // ── State ──
  getStateOfAssessment(): string {
    const currentState = localStorage.getItem('currentState');
    if (!currentState) return '';
    try {
      return JSON.parse(currentState)['toDisplay'];
    } catch (_) {
      return '';
    }
  }

  // ── Fullscreen ──
  enterFullscreen(): void {
    const el = document.documentElement;
    if (el.requestFullscreen) {
      el.requestFullscreen()
        .then(() => { this.isFullscreen = true; })
        .catch(() => { this.isFullscreen = true; });
    } else {
      this.isFullscreen = true;
    }
  }

  exitFullscreen(): void {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    this.isFullscreen = false;
  }

  // ── Loading ──
  showLoading(text: string = 'Progressing...'): void {
    this.loadingText = text;
    this.isLoading = true;
  }

  hideLoading(): void {
    this.isLoading = false;
  }

  // ── Question transition animation ──
  triggerQuestionTransition(callback: () => void): void {
    this.questionExiting = true;
    setTimeout(() => {
      if (this.integrityFailureSubmitted) {
        this.questionExiting = false;
        return;
      }
      this.questionExiting = false;
      callback();
      this.questionAnimating = true;
      setTimeout(() => { this.questionAnimating = false; }, 400);
    }, 280);
  }

  // ── Navigation ──
  async onNext(): Promise<void> {
    if (!this.hasSupportedAssessmentViewport()) {
      this.isUnsupportedScreen = true;
      this.cleanupTimers();
      this.snackBar.open(
        'Please use a laptop or desktop to begin the assessment.',
        'Dismiss',
        { duration: 5000 }
      );
      return;
    }

    const currentAssessmentState = JSON.parse(localStorage.getItem("currentState"));
    const sessionId = localStorage.getItem("sessionId");
    switch (currentAssessmentState["nextStep"]) {
      case 'pursue-session':
        if (!await this.requestCameraAccess()) return;
        this.pursueSession(currentAssessmentState, sessionId, 'rule-ack', {});
        break;
      default:
        window.alert("Cannot progress, please try again later");
    }
  }

  private hasSupportedAssessmentViewport(): boolean {
    return isAssessmentViewportSupported(window.innerWidth);
  }

  async enableCameraAndResume(): Promise<void> {
    if (this.isTimerRecoveryLoading || this.timerRecoveryError) return;
    if (!await this.requestCameraAccess()) return;
    this.cameraRequiredForResume = false;
    const sessionId = localStorage.getItem('sessionId');
    if (sessionId) this.startProctoringCaptureSchedule(sessionId);
    this.startCountdown(this.totalTime);
    this.startQuestionTimer(false);
    this.enterFullscreen();
  }

  private async requestCameraAccess(): Promise<boolean> {
    const video = this.proctoringVideo?.nativeElement;
    if (!video || this.isRequestingCamera) return false;

    this.isRequestingCamera = true;
    this.cameraErrorMessage = '';
    this.showLoading('Requesting Camera Access...');
    try {
      await this.proctoringService.requestCamera(video);
      this.isCameraActive = true;
      return true;
    } catch (error) {
      console.error('Camera permission was not granted', error);
      this.cameraErrorMessage =
        'Camera access is required to begin the assessment. Allow camera access in your browser and try again.';
      this.snackBar.open(this.cameraErrorMessage, 'Dismiss', { duration: 7000 });
      return false;
    } finally {
      this.isRequestingCamera = false;
      this.hideLoading();
    }
  }

  private startProctoringCaptureSchedule(sessionId: string): void {
    const userId = this.authService.getCurrentUser()?.userId || localStorage.getItem('userId');
    if (!userId || !sessionId) {
      this.cameraErrorMessage = 'Unable to initialize camera checks for this assessment.';
      return;
    }
    const storedStart = Number(localStorage.getItem(PROCTORING_STARTED_AT_KEY));
    const assessmentStartedAt = Number.isFinite(storedStart) && storedStart > 0
      ? storedStart
      : Date.now();
    localStorage.setItem(PROCTORING_STARTED_AT_KEY, String(assessmentStartedAt));
    this.proctoringService.startCaptureSchedule(userId, sessionId, assessmentStartedAt);
  }

  private stopProctoring(): void {
    this.proctoringService.stop();
    this.isCameraActive = false;
  }

  pursueSession(currState: any, sessionId: string, type: string, metadata: any): void {
    switch (type) {

      case 'rule-ack':
        this.showLoading('Starting Assessment...');
        const startObj = { sessionId, userId: localStorage.getItem('userId') };
        this.attempterService.pursueAssessment(startObj).subscribe((data) => {
          if (this.integrityFailureSubmitted) {
            this.hideLoading();
            return;
          }
          if (data) {
            this.applyAssessmentTiming(data);
            if (this.isCompletedNextStep(data)) {
              this.hideLoading();
              this.showCompletionScreen(currState, sessionId);
              return;
            }

            if (!this.proctoringService.isCameraActive) {
              this.hideLoading();
              this.cameraErrorMessage =
                'Camera access was interrupted before the assessment could begin. Please allow access and try again.';
              this.snackBar.open(this.cameraErrorMessage, 'Dismiss', { duration: 7000 });
              return;
            }

            this.enterFullscreen();
            this.startProctoringCaptureSchedule(sessionId);
            this.triggerQuestionTransition(() => {
              this.setCurrentQuestionState(currState, data['nextStep']);
            });
            this.startCountdown(this.totalTime);
            this.startQuestionTimer();
            this.hideLoading();
          } else {
            this.hideLoading();
            this.stopProctoring();
            window.alert("Unable to start the assessment, please try again.");
          }
        });
        break;

      case 'submit-answer':
        this.showLoading('Progressing...');
        this.attempterService.pursueAssessment(metadata).subscribe((data) => {
          if (this.integrityFailureSubmitted) {
            this.hideLoading();
            return;
          }
          if (data) {
            if (this.isCompletedNextStep(data)) {
              this.hideLoading();
              this.showCompletionScreen(currState, sessionId);
              return;
            }

            this.triggerQuestionTransition(() => {
              this.setCurrentQuestionState(currState, data['nextStep']);
            });
            this.startQuestionTimer();
            this.hideLoading();
          } else {
            this.hideLoading();
            window.alert("Unable to proceed further, please try again.");
          }
        });
        break;

      case 'section-time-out':
        this.showLoading('Evaluating Section...');
        const evaluateBody = {
          "sessionRequest": "evaluate",
          "userId": localStorage.getItem("userId"),
          sessionId
        };
        this.attempterService.pursueAssessment(evaluateBody).subscribe((data: any) => {
          this.hideLoading();
          if (this.integrityFailureSubmitted) return;
          if (data) {
            if (this.isCompletedNextStep(data)) {
              this.pursueSession(currState, sessionId, 'session-end', {});
            } else {
              this.triggerQuestionTransition(() => {
                this.setCurrentQuestionState(currState, data['nextStep']);
              });
              this.startCountdown(this.totalTime);
              this.startQuestionTimer();
            }
          }
        });
        break;

      case 'session-end':
        this.showCompletionScreen(currState, sessionId);
        break;

      default:
        alert('Unable to progress, please try again later');
    }
  }

  showCompletionScreen(currState: any, sessionId: string, completedSession?: any): void {
    if (this.integrityFailureSubmitted) return;
    this.cleanupTimers();
    this.stopProctoring();
    this.clearPersistedAssessmentTiming();
    this.exitFullscreen();
    this.completedAnsweredCount = 0;
    this.completionError = '';

    currState['toDisplay'] = 'completed';
    localStorage.setItem('currentState', JSON.stringify(currState));

    const userId = localStorage.getItem('userId') || this.authService.getCurrentUser()?.userId;
    if (!userId || !sessionId) {
      this.completionError = 'Unable to load the completed assessment summary.';
      return;
    }

    if (completedSession) {
      this.completedAnsweredCount = completedSession?.answeredQuestion?.length || 0;
      return;
    }

    this.showLoading('Loading Summary...');
    this.adminApi.getUserScores(userId).subscribe({
      next: (res) => {
        const sessions = this.getScoreSessions(res);
        const completedSession = sessions.find((session: any) => session.sessionId === sessionId);
        this.completedAnsweredCount = completedSession?.answeredQuestion?.length || 0;
        this.hideLoading();
      },
      error: (err) => {
        console.error('Failed to load completed assessment summary', err);
        this.completionError = 'Unable to load the completed assessment summary.';
        this.hideLoading();
      }
    });
  }

  isCompletedNextStep(data: any): boolean {
    return data?.nextStep?.state === 'COMPLETED';
  }

  setCurrentQuestionState(currState: any, nextStep: any): void {
    if (!nextStep?.questionId) {
      console.error('Assessment response did not include a valid question nextStep', nextStep);
      return;
    }

    currState['toDisplay'] = 'question';
    currState['currentQuestion'] = nextStep;
    this.attempterService.publishQuestionObject(JSON.stringify(nextStep));
    this.attempterService.publishSelectOptions('');
    localStorage.setItem('currentState', JSON.stringify(currState));
  }

  onRestartAssessment(): void {
    const sessionId = localStorage.getItem('sessionId');
    const userId = localStorage.getItem('userId') || this.authService.getCurrentUser()?.userId;

    if (!userId || !sessionId) {
      this.goToAssessments();
      return;
    }

    this.showLoading('Recovering Session...');
    this.adminApi.getUserScores(userId).subscribe({
      next: (res) => {
        const sessions = this.getScoreSessions(res);
        const currentSession = sessions.find((session: any) => session.sessionId === sessionId);

        if (!currentSession) {
          this.hideLoading();
          this.goToAssessments();
          return;
        }

        const assessmentState = (currentSession.assessmentState || '').toUpperCase();
        if (assessmentState === 'COMPLETED') {
          this.hideLoading();
          this.showCompletionScreen(this.createAssessmentState(), sessionId, currentSession);
          return;
        }

        if (assessmentState === 'FAILED') {
          this.hideLoading();
          this.integrityFailureSubmitted = true;
          this.integrityFailureMessage = 'This assessment attempt was ended after an integrity violation.';
          const failedState = this.createAssessmentState();
          failedState.toDisplay = 'failed';
          localStorage.setItem('currentState', JSON.stringify(failedState));
          return;
        }

        if (assessmentState === 'INPROGRESS') {
          this.recoverInProgressSession(userId, sessionId, currentSession);
          return;
        }

        this.hideLoading();
        this.goToAssessments();
      },
      error: (err) => {
        console.error('Failed to recover assessment session', err);
        this.hideLoading();
        this.goToAssessments();
      }
    });
  }

  recoverInProgressSession(userId: string, sessionId: string, session: any): void {
    this.attempterService.pursueAssessment({ userId, sessionId }).subscribe({
      next: (data: any) => {
        this.hideLoading();
        const nextStep = data?.nextStep;

        if (nextStep?.state === 'COMPLETED') {
          this.showCompletionScreen(this.createAssessmentState(), sessionId, session);
          return;
        }

        if (nextStep?.questionId) {
          this.resumeQuestion(nextStep, sessionId);
          return;
        }

        if (session?.currentQuestion?.questionId) {
          this.resumeQuestion(session.currentQuestion, sessionId);
          return;
        }

        this.goToAssessments();
      },
      error: (err) => {
        console.error('Failed to pursue recovered assessment session', err);
        this.hideLoading();

        if (session?.currentQuestion?.questionId) {
          this.resumeQuestion(session.currentQuestion, sessionId);
          return;
        }

        this.goToAssessments();
      }
    });
  }

  resumeQuestion(question: any, sessionId: string): void {
    const currentState = this.createAssessmentState();
    currentState['currentQuestion'] = question;
    localStorage.setItem('sessionId', sessionId);
    localStorage.setItem('currentState', JSON.stringify(currentState));
    this.attempterService.publishQuestionObject(JSON.stringify(question));
    this.attempterService.publishSelectOptions('');
    this.enterFullscreen();
    this.startCountdown(this.totalTime);
    this.startQuestionTimer(false);
  }

  createAssessmentState(): any {
    return {
      toDisplay: 'question',
      nextStep: 'pursue-session'
    };
  }

  getScoreSessions(res: any): any[] {
    return Array.isArray(res?.allUserScores)
      ? res.allUserScores
      : Array.isArray(res)
        ? res
        : res
          ? [res]
          : [];
  }

  private setAssessmentDurationMinutes(value: unknown): boolean {
    const minutes = Number(value);
    if (!Number.isFinite(minutes) || minutes <= 0) return false;
    this.totalTime = Math.round(minutes * 60);
    localStorage.setItem(ASSESSMENT_DURATION_SECONDS_KEY, String(this.totalTime));
    if (!localStorage.getItem(ASSESSMENT_ENDS_AT_KEY)) {
      this.currentMinutes = Math.floor(this.totalTime / 60);
      this.currentSeconds = this.totalTime % 60;
    }
    return true;
  }

  private restoreAssessmentDurationFromStorage(): void {
    const seconds = Number(localStorage.getItem(ASSESSMENT_DURATION_SECONDS_KEY));
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    this.totalTime = seconds;
    this.updateOverallTimerDisplay(this.readStoredTimestamp(ASSESSMENT_ENDS_AT_KEY));
  }

  private applyAssessmentTiming(source: any): boolean {
    const durationRestored = this.setAssessmentDurationMinutes(
      source?.assessmentTimeline ?? source?.assessement?.assessmentTimeline
    ) || this.totalTime > 0;

    const startedAt = Date.parse(source?.assessmentStartedAt || '');
    if (Number.isFinite(startedAt)) {
      localStorage.setItem(PROCTORING_STARTED_AT_KEY, String(startedAt));
    }

    const endsAt = Date.parse(source?.assessmentEndsAt || '');
    if (Number.isFinite(endsAt)) {
      localStorage.setItem(ASSESSMENT_ENDS_AT_KEY, String(endsAt));
    } else if (durationRestored && !this.readStoredTimestamp(ASSESSMENT_ENDS_AT_KEY)) {
      // Compatibility for sessions created before server-side deadlines existed.
      localStorage.setItem(ASSESSMENT_ENDS_AT_KEY, String(Date.now() + (this.totalTime * 1000)));
    }
    return durationRestored;
  }

  private restoreAssessmentTimingFromBackend(userId: string | null, sessionId: string | null): void {
    if (!userId || !sessionId) {
      this.timerRecoveryError = 'Unable to identify the active assessment session.';
      return;
    }

    this.isTimerRecoveryLoading = true;
    this.timerRecoveryError = '';
    this.adminApi.getUserScores(userId).subscribe({
      next: (response) => {
        const session = this.getScoreSessions(response)
          .find(item => String(item?.sessionId) === String(sessionId));
        if (!session || !this.applyAssessmentTiming(session)) {
          this.timerRecoveryError =
            'Unable to restore the assessment timer. Please contact support before continuing.';
        }
        this.isTimerRecoveryLoading = false;
      },
      error: (error) => {
        console.error('Failed to restore assessment timing', error);
        if (this.totalTime <= 0 || !this.readStoredTimestamp(ASSESSMENT_ENDS_AT_KEY)) {
          this.timerRecoveryError =
            'Unable to restore the assessment timer. Please contact support before continuing.';
        }
        this.isTimerRecoveryLoading = false;
      }
    });
  }

  private readStoredTimestamp(key: string): number | null {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  private clearPersistedAssessmentTiming(): void {
    localStorage.removeItem(PROCTORING_STARTED_AT_KEY);
    localStorage.removeItem(ASSESSMENT_DURATION_SECONDS_KEY);
    localStorage.removeItem(ASSESSMENT_ENDS_AT_KEY);
    localStorage.removeItem(QUESTION_ENDS_AT_KEY);
  }

  goToAssessments(): void {
    this.stopProctoring();
    this.clearPersistedAssessmentTiming();
    localStorage.removeItem('sessionId');
    localStorage.removeItem('currentState');
    this.router.navigate(['/assesments']);
  }

  // ── Overall countdown ──
  startCountdown(time: number): void {
    if (this.timerSubscription) this.timerSubscription.unsubscribe();
    if (time <= 0) {
      this.timerRecoveryError = 'Unable to restore the assessment timer.';
      return;
    }

    this.totalTime = time;
    let endsAt = this.readStoredTimestamp(ASSESSMENT_ENDS_AT_KEY);
    if (!endsAt) {
      endsAt = Date.now() + (time * 1000);
      localStorage.setItem(ASSESSMENT_ENDS_AT_KEY, String(endsAt));
    }
    this.overallTimerExpiryHandled = false;

    const update = () => {
      this.updateOverallTimerDisplay(endsAt);
      if (this.currentMinutes === 0 && this.currentSeconds === 0 && !this.overallTimerExpiryHandled) {
        this.overallTimerExpiryHandled = true;
        if (this.timerSubscription) this.timerSubscription.unsubscribe();
        const currentState = JSON.parse(localStorage.getItem('currentState'));
        const sessionId = localStorage.getItem('sessionId');
        this.pursueSession(currentState, sessionId, 'section-time-out', {});
      }
    };

    update();
    if (!this.overallTimerExpiryHandled) {
      this.timerSubscription = interval(1000).subscribe(update);
    }
  }

  private updateOverallTimerDisplay(endsAt: number | null): void {
    if (!endsAt) {
      this.currentMinutes = Math.floor(this.totalTime / 60);
      this.currentSeconds = this.totalTime % 60;
      return;
    }
    const remaining = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
    this.currentMinutes = Math.floor(remaining / 60);
    this.currentSeconds = remaining % 60;
  }

  // ── Per-question countdown ──
  startQuestionTimer(resetDeadline: boolean = true): void {
    if (this.questionTimerSubscription) this.questionTimerSubscription.unsubscribe();
    let endsAt = resetDeadline ? null : this.readStoredTimestamp(QUESTION_ENDS_AT_KEY);
    if (!endsAt) {
      endsAt = Date.now() + (QUESTION_TIMER_SECONDS * 1000);
      localStorage.setItem(QUESTION_ENDS_AT_KEY, String(endsAt));
    }
    this.questionTimerExpiryHandled = false;

    const update = () => {
      this.questionTotalSeconds = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      this.questionMinutes = Math.floor(this.questionTotalSeconds / 60);
      this.questionSeconds = this.questionTotalSeconds % 60;
      const fraction = 1 - (this.questionTotalSeconds / QUESTION_TIMER_SECONDS);
      this.questionTimerDashOffset = fraction * RING_CIRCUMFERENCE;

      if (this.questionTotalSeconds === 0 && !this.questionTimerExpiryHandled) {
        this.questionTimerExpiryHandled = true;
        localStorage.removeItem(QUESTION_ENDS_AT_KEY);
        if (this.questionTimerSubscription) this.questionTimerSubscription.unsubscribe();
        this.submitWithCurrentSelections();
      }
    };

    update();
    if (!this.questionTimerExpiryHandled) {
      this.questionTimerSubscription = interval(1000).subscribe(update);
    }
  }

  /**
   * Auto-submit when the per-question timer expires.
   * Uses getSelectedOptionsSnapshot() to grab whatever the user had already
   * checked — so partial selections are never lost to a timeout.
   */
  submitWithCurrentSelections(): void {
    const currentState = JSON.parse(localStorage.getItem('currentState'));
    const sessionId = localStorage.getItem('sessionId');
    const userId = localStorage.getItem('userId');
    const questionId = currentState?.currentQuestion?.questionId || null;

    let selectedAnswers: any[] = [];
    try {
      const snapshot = this.attempterService.getSelectedOptionsSnapshot();
      if (snapshot) {
        const parsed = JSON.parse(snapshot);
        // Guard: only use selections that belong to the current question
        if (parsed?.questionId === questionId && Array.isArray(parsed?.selectedOptions)) {
          selectedAnswers = parsed.selectedOptions;
        }
      }
    } catch (_) { /* snapshot empty or malformed — fall back to [] */ }

    const submitBody = {
      userId,
      sessionId,
      "sessionRequest": "response",
      "answerResp": selectedAnswers,
      "questionId": questionId,
      "metadata": { "timeTaken": QUESTION_TIMER_SECONDS }
    };
    this.pursueSession(currentState, sessionId, 'submit-answer', submitBody);
  }

  // ── Manual answer submission ──
  onAnswer(): void {
    const currentState = JSON.parse(localStorage.getItem('currentState'));
    const sessionId = localStorage.getItem('sessionId');
    const userId = localStorage.getItem('userId');
    const questionId = currentState?.currentQuestion?.questionId || null;
    const timeTaken = QUESTION_TIMER_SECONDS - this.questionTotalSeconds;

    let selectedAnswers: any[] = [];
    try {
      const snapshot = this.attempterService.getSelectedOptionsSnapshot();
      if (snapshot) {
        const parsed = JSON.parse(snapshot);
        if (parsed?.questionId === questionId && Array.isArray(parsed?.selectedOptions)) {
          selectedAnswers = parsed.selectedOptions;
        }
      }
    } catch (_) { /* no usable selection snapshot */ }

    if (selectedAnswers.length === 0) {
      this.snackBar.open('No answer selected. Please select an option before submitting.', 'Dismiss', {
        duration: 3000
      });
      return;
    }

    // Cancel the question timer — user submitted a valid answer before it expired
    if (this.questionTimerSubscription) this.questionTimerSubscription.unsubscribe();

    const submitAnswerBody = {
      userId,
      sessionId,
      "sessionRequest": "response",
      "answerResp": selectedAnswers,
      "questionId": questionId,
      "metadata": { timeTaken }
    };
    this.pursueSession(currentState, sessionId, 'submit-answer', submitAnswerBody);
  }
}
